import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, setDoc, addDoc, query, where, Timestamp } from "firebase/firestore";
import { defaultSettings, defaultItems, summarizeAttendance, calcPayroll, periodOf, yen } from "../utils/payroll";
import PayrollSettings from "./PayrollSettings";
import PayrollItems from "./PayrollItems";
import WageLedger from "./WageLedger";
import MyPayslips from "./MyPayslips";

// 給与計算。自動計算で一覧表に金額を入れ、表の上で直接直してから
// 「一時保存」または「確定」する。確定済みを直す場合は理由の入力を必須にし、
// payrolls/{id}/history に追記のみで記録を残す（後から書き換え・削除はできない）。

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const docIdOf = (companyId, ym, uid) => `${companyId}_${ym}_${uid}`;
const sumOf = (amounts, items) => items.reduce((a, it) => a + Number(amounts?.[it.key] || 0), 0);

function Payroll({ user, companyId, isPaid = null, isCompanyAdmin = false, onNavigate }) {
  const [tab, setTab]             = useState("calc");
  const [yearMonth, setYearMonth] = useState(thisMonth);
  const [settings, setSettings]   = useState(null);
  const [items, setItems]         = useState(defaultItems());
  const [calendar, setCalendar]   = useState([]);
  const [shiftCells, setShiftCells] = useState({});   // シフト表（uid → 日 → パターンkey）
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [rows, setRows]           = useState([]);
  const [checked, setChecked]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState("");
  const [error, setError]         = useState("");
  const [message, setMessage]     = useState("");
  const [askReason, setAskReason] = useState(null);  // { targets, mode }
  const [historyOf, setHistoryOf] = useState(null);
  const [attendanceOf, setAttendanceOf] = useState(null);
  const [payslipOf, setPayslipOf]       = useState(null);   // 明細を確認する社員

  useEffect(() => { load(); }, [yearMonth]);

  const load = async () => {
    setLoading(true); setError(""); setMessage("");
    try {
      const sSnap = await getDoc(doc(db, "payrollSettings", companyId));
      const st = sSnap.exists() ? { ...defaultSettings(companyId), ...sSnap.data() } : defaultSettings(companyId);
      setSettings(st);
      const itemList = Array.isArray(st.items) && st.items.length > 0 ? st.items : defaultItems();
      setItems(itemList);

      try {
        const cSnap = await getDoc(doc(db, "companyCalendars", companyId));
        setCalendar(cSnap.exists() ? (cSnap.data().days || []) : []);
      } catch (e) {
        console.warn("会社カレンダーを読めなかったため曜日の設定で判定します:", e);
        setCalendar([]);
      }

      setShiftPatterns(Array.isArray(st.shiftPatterns) ? st.shiftPatterns : []);
      try {
        const shSnap = await getDoc(doc(db, "shifts", `${companyId}_${yearMonth}`));
        setShiftCells(shSnap.exists() ? (shSnap.data().cells || {}) : {});
      } catch (e) {
        console.warn("シフト表を読めなかったため曜日の設定で判定します:", e);
        setShiftCells({});
      }

      const uSnap = await getDocs(query(
        collection(db, "users"),
        where("companyId", "==", companyId),
        where("status", "==", "approved")
      ));
      const users = uSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.nameKana || a.name || "").localeCompare(b.nameKana || b.name || "", "ja"));

      const pSnap = await getDocs(query(collection(db, "payrollProfiles"), where("companyId", "==", companyId)));
      const profiles = {};
      pSnap.docs.forEach(d => { profiles[d.id] = d.data(); });

      const rSnap = await getDocs(query(
        collection(db, "payrolls"),
        where("companyId", "==", companyId),
        where("yearMonth", "==", yearMonth)
      ));
      const saved = {};
      rSnap.docs.forEach(d => { saved[d.data().uid] = { id: d.id, ...d.data() }; });

      setRows(users.map(u => {
        const sv = saved[u.id] || null;
        const amounts = {};
        itemList.forEach(it => { amounts[it.key] = Number(sv?.amounts?.[it.key] || 0); });
        return {
          user: u,
          profile: profiles[u.id] || null,
          saved: sv,
          status: sv ? sv.status : "none",
          attendance: sv?.attendance || null,
          amounts,
          dirty: false,
        };
      }));
      setChecked({});
    } catch (e) {
      console.error("給与計算データの取得に失敗しました:", e);
      setError("データの読み込みに失敗しました。権限をご確認ください。");
    } finally {
      setLoading(false);
    }
  };

  // 勤怠を集計して金額を組み立てる（保存はしない）
  const computeRow = async (row) => {
    if (!row.profile) throw new Error("給与情報が未登録です");
    const { from, to } = periodOf(yearMonth, settings);
    // 対象期間だけを読む。全期間を取ると年々読み取り量が増えるため。
    const aSnap = await getDocs(query(
      collection(db, "attendance"),
      where("uid", "==", row.user.id),
      where("timestamp", ">=", Timestamp.fromDate(from)),
      where("timestamp", "<=", Timestamp.fromDate(to))
    ));
    const punches = aSnap.docs.map(d => d.data())
      .filter(a => !a.cancelled)          // 管理者が取り消した打刻は集計に入れない
      .filter(a => a.timestamp?.toDate)
      .map(a => ({ type: a.type, timestamp: a.timestamp.toDate() }));

    // その社員のシフトを「日付 → 勤務パターン」に展開する
    const shiftByDate = {};
    const myShift = shiftCells[row.user.id] || {};
    Object.keys(myShift).forEach(day => {
      const v = myShift[day];
      // 勤務パターンのkeyか、時間を直接指定した object のどちらか
      const sp = (v && typeof v === "object") ? v : shiftPatterns.find(p => p.key === v);
      if (!sp) return;
      const [yy, mm] = yearMonth.split("-");
      shiftByDate[`${yy}-${mm}-${String(day).padStart(2, "0")}`] = sp;
    });

    const summary = summarizeAttendance(punches, settings, calendar, row.profile, shiftByDate);
    const result  = calcPayroll({ profile: row.profile, summary, settings, yearMonth });

    // 自動計算を割り当てた項目だけ上書きし、手入力の項目は今の値を残す
    const amounts = { ...row.amounts };
    items.forEach(it => {
      if (it.auto) amounts[it.key] = Number(result.auto[it.auto] || 0);
    });
    return { ...row, attendance: result.attendance, amounts, dirty: true };
  };

  const runCalc = async (targetIds) => {
    setBusy("calc"); setError(""); setMessage("");
    const failed = [];
    const next = [...rows];
    for (let i = 0; i < next.length; i++) {
      if (!targetIds.includes(next[i].user.id)) continue;
      if (next[i].status === "confirmed") continue;   // 確定済みは自動計算の対象外
      try { next[i] = await computeRow(next[i]); }
      catch (e) { failed.push(`${next[i].user.name || next[i].user.email}：${e.message}`); }
    }
    setRows(next);
    setBusy("");
    if (failed.length > 0) setError(`計算できなかった社員がいます — ${failed.join(" / ")}`);
    else setMessage("自動計算しました。内容を確認し、必要なら表を直してから保存してください。");
  };

  const setCell = (uid, key, value) => {
    setRows(rs => rs.map(r => r.user.id !== uid ? r : {
      ...r,
      amounts: { ...r.amounts, [key]: value === "" ? 0 : Number(value) },
      dirty: true,
    }));
  };

  // 変更点の差分（確定済みの修正記録に使う）
  const diffOf = (row) => {
    const out = [];
    items.forEach(it => {
      const b = Number(row.saved?.amounts?.[it.key] || 0), a = Number(row.amounts?.[it.key] || 0);
      if (b !== a) out.push({ label: `${it.type}・${it.name}`, before: yen(b), after: yen(a) });
    });
    return out;
  };

  const write = async (targets, mode, reason) => {
    setBusy(mode); setError(""); setMessage("");
    try {
      const now = Timestamp.now();
      for (const row of targets) {
        const id = docIdOf(companyId, yearMonth, row.user.id);
        const wasConfirmed = row.status === "confirmed";
        const changes = diffOf(row);
        const totalE = sumOf(row.amounts, earnItems), totalD = sumOf(row.amounts, dedItems);

        await setDoc(doc(db, "payrolls", id), {
          companyId, yearMonth,
          uid: row.user.id,
          name: row.user.name || "",
          memberNo: row.user.memberNo || "",
          email: row.user.email || "",
          status: mode === "confirm" ? "confirmed" : (wasConfirmed ? "confirmed" : "draft"),
          attendance: row.attendance || null,
          amounts: row.amounts,
          // 項目名を後から変えても記録が読めるよう、そのときの定義を一緒に残す
          items: items.map(it => ({ key: it.key, name: it.name, type: it.type })),
          totalEarnings: totalE,
          totalDeductions: totalD,
          netPay: totalE - totalD,
          updatedAt: now,
          updatedBy: user.email,
          ...(mode === "confirm" && !wasConfirmed ? { confirmedAt: now, confirmedBy: user.email } : {}),
          ...(wasConfirmed && changes.length > 0 ? { amendedAt: now, amendedBy: user.email } : {}),
        }, { merge: true });

        // 記録を残すのは、確定したときと、確定済みを修正したとき
        if (mode === "confirm" && !wasConfirmed) {
          await addDoc(collection(db, "payrolls", id, "history"), {
            companyId, uid: row.user.id, action: "確定",
            changedAt: now, changedBy: user.email, reason: reason || "", changes,
          });
        } else if (wasConfirmed && changes.length > 0) {
          await addDoc(collection(db, "payrolls", id, "history"), {
            companyId, uid: row.user.id, action: "確定後の修正",
            changedAt: now, changedBy: user.email, reason: reason || "", changes,
          });
        }
      }
      await load();
      setMessage(mode === "confirm" ? `${targets.length}件を確定しました。` : `${targets.length}件を一時保存しました。`);
    } catch (e) {
      console.error("保存に失敗しました:", e);
      setError("保存に失敗しました。権限をご確認ください。");
    } finally {
      setBusy("");
    }
  };

  // 保存の入口。確定済みを触っている場合は理由を先に聞く。
  const startWrite = (mode) => {
    const ids = selectedIds();
    const targets = rows.filter(r => ids.includes(r.user.id) && r.profile &&
      (r.dirty || (mode === "confirm" && r.status === "draft")));
    if (targets.length === 0) { setError("保存する対象がありません。"); return; }
    if (mode === "confirm" && !window.confirm(`${targets.length}件を確定します。確定後の修正には理由の記録が必要になります。よろしいですか？`)) return;

    const amended = targets.filter(r => r.status === "confirmed" && diffOf(r).length > 0);
    if (amended.length > 0) setAskReason({ targets, mode });
    else write(targets, mode, "");
  };

  const selectedIds = () => {
    const ids = rows.filter(r => checked[r.user.id]).map(r => r.user.id);
    return ids.length > 0 ? ids : rows.map(r => r.user.id);   // 未選択なら全員
  };

  const allChecked = rows.length > 0 && rows.every(r => checked[r.user.id]);
  const toggleAll = () => {
    if (allChecked) setChecked({});
    else { const n = {}; rows.forEach(r => { n[r.user.id] = true; }); setChecked(n); }
  };

  // 打刻漏れの日数。自動計算したときだけ分かる。
  const incompleteCount = (r) => (r.attendance?.incompleteDays || []).length;
  const totalIncomplete = rows.reduce((a, r) => a + incompleteCount(r), 0);

  const earnItems = items.filter(it => it.type === "支給");
  const dedItems  = items.filter(it => it.type === "控除");

  const statusLabel = (r) =>
    r.status === "confirmed" ? "確定済み" : r.status === "draft" ? "一時保存" : "未計算";
  const statusStyle = (r) =>
    r.status === "confirmed" ? st.chipDone : r.status === "draft" ? st.chipDraft : st.chipNone;

  // 給与設定・項目設定は無料プランでも開ける（勤怠の集計にも使う設定のため）。
  // 計算そのものだけを有料プラン限定にする。
  if (isPaid === false && tab === "calc") {
    return (
      <div style={st.container}>
        <h2 style={st.title}>🧮 給与計算</h2>
        <div style={st.lockBox}>
          <div style={{ fontSize: "44px", marginBottom: "12px" }}>🔒</div>
          <h3 style={{ color: "#2c3e50", margin: "0 0 10px" }}>有料プラン限定の機能です</h3>
          <p style={{ color: "#7f8c8d", lineHeight: "1.8", margin: "0 0 20px" }}>
            勤怠の打刻から給与を自動計算し、明細を確定できる機能です。<br />
            有料プランにご登録いただくとご利用いただけます。<br />
            給与明細の閲覧と、下の「給与設定」「項目設定」は無料プランでもお使いいただけます。
          </p>
          <div style={st.tabs}>
            <button style={st.tab} onClick={() => setTab("items")}>項目設定</button>
            <button style={st.tab} onClick={() => setTab("settings")}>給与設定</button>
          </div>
          {isCompanyAdmin && onNavigate && (
            <button style={st.upgradeBtn} onClick={() => onNavigate("billing")}>お支払い設定を開く</button>
          )}
          {!isCompanyAdmin && (
            <p style={{ color: "#95a5a6", fontSize: "13px", margin: 0 }}>会社の管理者にお問い合わせください。</p>
          )}
        </div>
      </div>
    );
  }

  if (isPaid === null) {
    return <div style={st.container}><p style={st.empty}>読み込み中...</p></div>;
  }

  if (tab === "settings") {
    return <PayrollSettings companyId={companyId} user={user} onBack={() => { setTab("calc"); load(); }} />;
  }
  if (tab === "items") {
    return <PayrollItems companyId={companyId} user={user} onBack={() => { setTab("calc"); load(); }} />;
  }
  if (tab === "ledger") {
    return <WageLedger companyId={companyId} onBack={() => setTab("calc")} />;
  }

  const selectedCount = rows.filter(r => checked[r.user.id]).length;

  return (
    <div style={st.container}>
      <h2 style={st.title}>🧮 給与計算</h2>

      <div style={st.tabs}>
        <button style={{ ...st.tab, ...st.active }}>計算</button>
        <button style={st.tab} onClick={() => setTab("ledger")}>賃金台帳</button>
        <button style={st.tab} onClick={() => setTab("items")}>項目設定</button>
        <button style={st.tab} onClick={() => setTab("settings")}>給与設定</button>
      </div>

      {settings && settings.incomeTaxMode === "auto" && !settings.taxTableVerified && (
        <div style={st.warn}>
          ⚠️ 所得税の税額表がまだ確認されていません。国税庁の「源泉徴収税額表」と照合し、給与設定で「確認した」にチェックを入れてください。
          照合前の金額をそのまま支給に使わないでください。
        </div>
      )}

      <div style={st.toolbar}>
        <div style={st.toolLeft}>
          <label style={st.label}>対象年月</label>
          <input style={st.month} type="month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} />
          {settings && (
            <div style={st.period}>
              集計期間 {periodOf(yearMonth, settings).from.toLocaleDateString("ja-JP")}
              〜{periodOf(yearMonth, settings).to.toLocaleDateString("ja-JP")}
            </div>
          )}
        </div>
        <div style={st.toolRight}>
          <button style={st.calcBtn} disabled={busy !== "" || loading}
            onClick={() => runCalc(selectedIds())}>
            {busy === "calc" ? "計算中..." : selectedCount > 0 ? `選択${selectedCount}名を自動計算` : "全員を自動計算"}
          </button>
          <button style={st.draftBtn} disabled={busy !== ""} onClick={() => startWrite("draft")}>
            {busy === "draft" ? "保存中..." : "一時保存"}
          </button>
          <button style={st.confirmBtn} disabled={busy !== ""} onClick={() => startWrite("confirm")}>
            {busy === "confirm" ? "処理中..." : "確定"}
          </button>
        </div>
      </div>

      {totalIncomplete > 0 && (
        <div style={st.warn}>
          ⚠️ 打刻が揃っていない日が <strong>{totalIncomplete}日</strong> あります。
          その日は労働時間に加算されておらず、そのままでは給与から抜け落ちます。
          該当する社員の「明細」を開いて内容を確認し、必要なら勤怠を直してから計算し直してください。
        </div>
      )}

      {error   && <p style={st.error}>{error}</p>}
      {message && <p style={st.ok}>{message}</p>}

      {loading ? <p style={st.empty}>読み込み中...</p> : rows.length === 0 ? (
        <p style={st.empty}>承認済みの社員がいません</p>
      ) : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={{ ...st.th, ...st.thCheck }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} style={st.cb} />
                </th>
                <th style={{ ...st.th, ...st.thName }}>社員</th>
                <th style={st.th}>状態</th>
                <th style={st.thNum}>出勤日</th>
                <th style={st.thNum}>所定内h</th>
                <th style={st.thNum}>法定内h</th>
                <th style={st.thNum}>残業h</th>
                <th style={st.thNum}>深夜h</th>
                <th style={st.thNum}>休日h</th>
                <th style={st.th}>勤怠</th>
                {earnItems.map(it => <th key={it.key} style={st.thPay}>{it.name}</th>)}
                <th style={st.thTotal}>支給計</th>
                {dedItems.map(it => <th key={it.key} style={st.thDed}>{it.name}</th>)}
                <th style={st.thTotal}>控除計</th>
                <th style={st.thNet}>差引支給額</th>
                <th style={st.th}>履歴</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const totalE = sumOf(r.amounts, earnItems), totalD = sumOf(r.amounts, dedItems);
                const locked = r.status === "confirmed";
                return (
                  <tr key={r.user.id} style={r.dirty ? st.trDirty : undefined}>
                    <td style={st.td}>
                      <input type="checkbox" style={st.cb}
                        checked={!!checked[r.user.id]}
                        onChange={e => setChecked({ ...checked, [r.user.id]: e.target.checked })} />
                    </td>
                    <td style={{ ...st.td, ...st.tdName }}>
                      <div style={st.name}>{r.user.name || r.user.email}</div>
                      {r.user.memberNo && <div style={st.memberNo}>{r.user.memberNo}</div>}
                      {!r.profile && <div style={st.noProfile}>給与情報 未登録</div>}
                    </td>
                    <td style={st.td}><span style={{ ...st.chip, ...statusStyle(r) }}>{statusLabel(r)}</span></td>
                    <td style={st.tdNum}>{r.attendance?.workDays ?? "－"}</td>
                    <td style={st.tdNum}>{r.attendance?.plainHours ?? "－"}</td>
                    <td style={st.tdNum}>{r.attendance?.withinOtHours ?? "－"}</td>
                    <td style={st.tdNum}>{r.attendance?.overtimeHours ?? "－"}</td>
                    <td style={st.tdNum}>{r.attendance?.nightHours ?? "－"}</td>
                    <td style={st.tdNum}>{r.attendance?.holidayHours ?? "－"}</td>
                    <td style={st.td}>
                      {r.attendance?.days
                        ? <button style={st.histBtn} onClick={() => setAttendanceOf(r)}>
                            明細{incompleteCount(r) > 0 ? ` ⚠${incompleteCount(r)}` : ""}
                          </button>
                        : <span style={{ color: "#bbb", fontSize: "11px" }}>－</span>}
                    </td>

                    {earnItems.map(it => (
                      <td key={it.key} style={st.tdCell}>
                        <input style={{ ...st.cell, ...(locked ? st.cellLocked : {}) }} type="number"
                          value={r.amounts?.[it.key] ?? 0} disabled={!r.profile}
                          onChange={e => setCell(r.user.id, it.key, e.target.value)} />
                      </td>
                    ))}
                    <td style={st.tdTotal}>{yen(totalE)}</td>

                    {dedItems.map(it => (
                      <td key={it.key} style={st.tdCell}>
                        <input style={{ ...st.cell, ...(locked ? st.cellLocked : {}) }} type="number"
                          value={r.amounts?.[it.key] ?? 0} disabled={!r.profile}
                          onChange={e => setCell(r.user.id, it.key, e.target.value)} />
                      </td>
                    ))}
                    <td style={st.tdTotal}>{yen(totalD)}</td>
                    <td style={st.tdNet}>{yen(totalE - totalD)}</td>
                    <td style={st.td}>
                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                        {r.saved && <button style={st.histBtn} onClick={() => setHistoryOf(r)}>履歴</button>}
                        {r.status === "confirmed" &&
                          <button style={st.histBtn} onClick={() => setPayslipOf(r)}>明細</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={st.note}>
        黄色の行は未保存の変更があります。確定済みの行を直すと、保存時に理由の入力を求められ、記録が残ります。
        社員を選択していないときは、全員が対象になります。
      </p>

      {askReason && (
        <ReasonModal
          count={askReason.targets.filter(r => r.status === "confirmed").length}
          onCancel={() => setAskReason(null)}
          onSubmit={(reason) => { const a = askReason; setAskReason(null); write(a.targets, a.mode, reason); }}
        />
      )}

      {historyOf && <HistoryModal row={historyOf} onClose={() => setHistoryOf(null)} />}

      {attendanceOf && <AttendanceModal row={attendanceOf} onClose={() => setAttendanceOf(null)} />}

      {payslipOf && (
        <div style={st.overlay}>
          <div style={{ ...st.modal, width: "700px" }}>
            <h3 style={st.modalTitle}>給与明細の確認</h3>
            <MyPayslips user={user} companyId={companyId}
              targetUid={payslipOf.user.id}
              targetName={payslipOf.user.name || payslipOf.user.email} />
            <div style={st.modalBtns}>
              <button style={st.cancelBtn} onClick={() => setPayslipOf(null)}>閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
function ReasonModal({ count, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>確定済みの給与を修正します</h3>
        <p style={st.modalLead}>
          確定済みが{count}件含まれています。修正の記録が残り、後から取り消すことはできません。
          理由を入力してください。
        </p>
        <textarea style={st.reason} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="例：通勤手当の計上漏れのため修正" />
        <div style={st.modalBtns}>
          <button style={st.saveBtn} disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>
            理由を記録して保存
          </button>
          <button style={st.cancelBtn} onClick={onCancel}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

// 日別の勤務時間。自動計算したときの内訳をそのまま表示する。
function AttendanceModal({ row, onClose }) {
  const att = row.attendance || {};
  const days = att.days || [];
  const bad  = days.filter(d => d.incomplete);
  const ok   = days.filter(d => !d.incomplete);
  const DOW  = ["日", "月", "火", "水", "木", "金", "土"];
  const hm = (m) => `${Math.floor((m || 0) / 60)}:${String((m || 0) % 60).padStart(2, "0")}`;

  return (
    <div style={st.overlay}>
      <div style={{ ...st.modal, width: "640px" }}>
        <h3 style={st.modalTitle}>勤務時間の明細：{row.user.name || row.user.email}</h3>
        <div style={st.attSummary}>
          出勤 {att.workDays ?? 0}日 ／ 所定内 {att.plainHours ?? 0}h ／
          法定内残業 {att.withinOtHours ?? 0}h ／ 法定外残業 {att.overtimeHours ?? 0}h
          {att.over60Hours > 0 && <> ／ うち60時間超 {att.over60Hours}h</>} ／
          深夜 {att.nightHours ?? 0}h ／ 休日 {att.holidayHours ?? 0}h
          {att.rawOvertimeHours != null && att.rawOvertimeHours !== att.overtimeHours && (
            <div style={{ marginTop: "4px", color: "#7f8c8d" }}>
              実績の法定外残業は {att.rawOvertimeHours}h ですが、みなし残業分を差し引いて {att.overtimeHours}h を手当の対象にしています。
            </div>
          )}
        </div>

        {bad.length > 0 && (
          <div style={st.warn}>
            ⚠️ 打刻が揃っていない日が {bad.length}日あります。労働時間に入っていません。
            <ul style={st.badList}>
              {bad.map(d => <li key={d.date}>{d.date}　{d.issue || "打刻が不完全です"}</li>)}
            </ul>
          </div>
        )}

        {ok.length === 0 ? <p style={st.empty}>集計できた日がありません</p> : (
          <div style={st.tableWrap}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>日付</th>
                  <th style={st.th}>曜日</th>
                  <th style={st.th}>出勤</th>
                  <th style={st.th}>退勤</th>
                  <th style={st.thNum}>休憩</th>
                  <th style={st.thNum}>所定</th>
                  <th style={st.thNum}>実働</th>
                  <th style={st.thNum}>法定内</th>
                  <th style={st.thNum}>残業</th>
                  <th style={st.thNum}>深夜</th>
                  <th style={st.th}>区分</th>
                </tr>
              </thead>
              <tbody>
                {ok.map(d => (
                  <tr key={d.date}>
                    <td style={st.td}>{d.date}</td>
                    <td style={st.td}>{DOW[d.dow] ?? ""}</td>
                    <td style={st.td}>{d.start}</td>
                    <td style={st.td}>{d.end}</td>
                    <td style={st.tdNum}>{d.breakMin ? `${d.breakMin}分` : "－"}</td>
                    <td style={st.tdNum}>{d.scheduledMin ? hm(d.scheduledMin) : "－"}</td>
                    <td style={st.tdNum}>{hm(d.minutes)}</td>
                    <td style={st.tdNum}>{d.withinOtMin ? hm(d.withinOtMin) : "－"}</td>
                    <td style={st.tdNum}>{d.legalOtMin ? hm(d.legalOtMin) : "－"}</td>
                    <td style={st.tdNum}>{d.nightMin ? hm(d.nightMin) : "－"}</td>
                    <td style={st.td}>
                      {d.isLegalHoliday ? "法定休日" : ""}
                      {d.crossesMidnight ? " 翌日まで" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p style={st.note}>
          この内訳は自動計算したときの勤怠をそのまま保存したものです。あとから打刻を直しても、
          確定した給与の根拠はこの内容のまま残ります。
        </p>

        <div style={st.modalBtns}>
          <button style={st.cancelBtn} onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ row, onClose }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "payrolls", row.saved.id, "history"));
        setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.changedAt - a.changedAt));
      } catch (e) {
        console.error("履歴の取得に失敗しました:", e);
        setHistory([]);
      }
    })();
  }, [row.saved.id]);

  const fmt = (ts) => ts?.toDate ? ts.toDate().toLocaleString("ja-JP") : "";

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <h3 style={st.modalTitle}>変更履歴：{row.user.name || row.user.email}</h3>
        {history === null ? <p style={st.empty}>読み込み中...</p>
          : history.length === 0 ? <p style={st.empty}>履歴はありません</p>
            : history.map(h => (
              <div key={h.id} style={st.histCard}>
                <div style={st.histHead}>
                  <strong>{h.action}</strong><span>{fmt(h.changedAt)} · {h.changedBy}</span>
                </div>
                {h.reason && <div style={st.histReason}>理由：{h.reason}</div>}
                {(h.changes || []).map((c, i) => (
                  <div key={i} style={st.histRow}>
                    <span style={st.histLabel}>{c.label}</span>
                    <span style={st.before}>{c.before}</span> → <span style={st.after}>{c.after}</span>
                  </div>
                ))}
              </div>
            ))}
        <div style={st.modalBtns}>
          <button style={st.cancelBtn} onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

const st = {
  container: { padding: "28px 24px", maxWidth: "100%", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "16px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "16px" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  active: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  warn: { backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", fontSize: "13px", color: "#8a5a00", lineHeight: "1.7" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", backgroundColor: "#fff", padding: "16px 20px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "14px" },
  toolLeft: {},
  toolRight: { display: "flex", gap: "8px", flexWrap: "wrap" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  month: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px" },
  period: { fontSize: "12px", color: "#7f8c8d", marginTop: "6px" },
  calcBtn: { padding: "10px 18px", backgroundColor: "#2980b9", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  draftBtn: { padding: "10px 18px", backgroundColor: "#fff", color: "#2c3e50", border: "1px solid #2c3e50", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  confirmBtn: { padding: "10px 18px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "10px" },
  ok: { color: "#27ae60", fontSize: "14px", marginBottom: "10px" },
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  note: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.8", marginTop: "12px" },
  tableWrap: { overflowX: "auto", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
  table: { borderCollapse: "collapse", fontSize: "13px", minWidth: "100%" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "left" },
  thCheck: { width: "36px" },
  thName: { minWidth: "150px" },
  thNum: { backgroundColor: "#2c3e50", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" },
  thPay: { backgroundColor: "#1f6f8b", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" },
  thDed: { backgroundColor: "#7b4b6b", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" },
  thTotal: { backgroundColor: "#34495e", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" },
  thNet: { backgroundColor: "#1e8449", color: "#fff", padding: "10px 8px", fontSize: "12px", whiteSpace: "nowrap", textAlign: "right" },
  trDirty: { backgroundColor: "#fffbe8" },
  td: { borderBottom: "1px solid #eee", padding: "6px 8px", verticalAlign: "middle" },
  tdName: { minWidth: "150px" },
  tdNum: { borderBottom: "1px solid #eee", padding: "6px 8px", textAlign: "right", color: "#555", whiteSpace: "nowrap" },
  tdCell: { borderBottom: "1px solid #eee", padding: "4px 5px" },
  tdTotal: { borderBottom: "1px solid #eee", padding: "6px 8px", textAlign: "right", fontWeight: "bold", color: "#2c3e50", whiteSpace: "nowrap", backgroundColor: "#f7f9fb" },
  tdNet: { borderBottom: "1px solid #eee", padding: "6px 8px", textAlign: "right", fontWeight: "bold", color: "#1e8449", whiteSpace: "nowrap", backgroundColor: "#f2faf5" },
  cell: { width: "96px", padding: "6px 8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px", textAlign: "right", boxSizing: "border-box" },
  cellLocked: { backgroundColor: "#fdf6f6", border: "1px solid #e8c9c9" },
  name: { fontWeight: "bold", color: "#2c3e50", whiteSpace: "nowrap" },
  memberNo: { fontSize: "11px", color: "#2980b9", fontFamily: "monospace" },
  noProfile: { fontSize: "11px", color: "#e67e22" },
  chip: { padding: "3px 9px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold", whiteSpace: "nowrap" },
  chipNone: { backgroundColor: "#f0f0f0", color: "#999" },
  chipDraft: { backgroundColor: "#f39c1222", color: "#f39c12" },
  chipDone: { backgroundColor: "#27ae6022", color: "#27ae60" },
  cb: { width: "16px", height: "16px" },
  histBtn: { padding: "4px 10px", backgroundColor: "#fff", color: "#2980b9", border: "1px solid #2980b9", borderRadius: "6px", cursor: "pointer", fontSize: "11px" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "40px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "520px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 10px", fontSize: "18px" },
  modalLead: { color: "#7f8c8d", fontSize: "13px", lineHeight: "1.8", marginTop: 0, marginBottom: "14px" },
  reason: { width: "100%", height: "90px", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box", resize: "vertical" },
  modalBtns: { display: "flex", gap: "12px", marginTop: "20px" },
  saveBtn: { padding: "10px 28px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  cancelBtn: { padding: "10px 24px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  histCard: { border: "1px solid #e6ebf0", borderRadius: "10px", padding: "12px 14px", marginBottom: "10px", backgroundColor: "#fafbfc" },
  histHead: { display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12px", color: "#7f8c8d", marginBottom: "6px", flexWrap: "wrap" },
  histReason: { fontSize: "13px", color: "#2c3e50", marginBottom: "6px" },
  histRow: { fontSize: "13px", padding: "2px 0", display: "flex", gap: "8px", flexWrap: "wrap" },
  histLabel: { color: "#7f8c8d", minWidth: "130px" },
  lockBox: { backgroundColor: "#fff", padding: "40px 32px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center", maxWidth: "620px", margin: "0 auto" },
  upgradeBtn: { padding: "12px 32px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  attSummary: { fontSize: "13px", color: "#2c3e50", backgroundColor: "#f7f9fb", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", lineHeight: "1.8" },
  badList: { margin: "8px 0 0", paddingLeft: "20px", lineHeight: "1.8" },
  before: { color: "#95a5a6", textDecoration: "line-through" },
  after: { color: "#2c3e50", fontWeight: "bold" },
};

export default Payroll;
